import { useEffect, useMemo, useState } from 'react'
import { tournamentApi } from '../services/api'
import PortalShell from './setup/PortalShell'
import { useLanguage } from '../context/LanguageContext'

/**
 * Merchant-facing bookings list. Course-scoped by the JWT — getBookings
 * controller filters by req.user.courses_id. Shows recent reservations with
 * a status filter, an optional date range, and click-to-expand for the full
 * itinerary (rounds + hotel + golfers + payment).
 *
 * Read-only for v1. Status update / cancel actions are reachable from the
 * existing admin endpoints (/play-stay/bookings/:id/status, /cancel) and
 * could be wired into row-actions in a follow-up.
 */
const STATUS_LABELS = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  needs_attention: 'Needs Attention',
  cancelled: 'Cancelled',
  completed: 'Completed',
  no_show: 'No-Show',
}

const STATUS_COLORS = {
  pending: { bg: '#fef3c7', fg: '#92400e' },
  confirmed: { bg: '#dcfce7', fg: '#166534' },
  needs_attention: { bg: '#fee2e2', fg: '#991b1b' },
  cancelled: { bg: '#f3f4f6', fg: '#374151' },
  completed: { bg: '#dbeafe', fg: '#1e40af' },
  no_show: { bg: '#fef3c7', fg: '#854d0e' },
}

// Format a YYYY-MM-DD (or Date) for display without TZ shift — same fix we
// applied server-side to the email template.
function fmtLocalDate(d, opts = { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) {
  if (!d) return '—'
  const s = typeof d === 'string' ? d.slice(0, 10) : new Date(d).toISOString().slice(0, 10)
  const [y, m, day] = s.split('-').map(Number)
  if (!y || !m || !day) return '—'
  return new Date(y, m - 1, day).toLocaleDateString('en-US', opts)
}

function fmtMoney(n) {
  return Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

// Quote a value for CSV per RFC 4180: wrap in quotes if it contains a comma,
// quote, or newline; double-up any embedded quotes.
function csvCell(v) {
  if (v === null || v === undefined) return ''
  const s = String(v)
  return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s
}

async function downloadBookingPdf(bookingId) {
  // PDF endpoint streams binary; need a blob fetch (not the json-shaped axios
  // helpers) so we get the raw bytes and can save-as. Auth header has to be
  // attached manually since we're bypassing the tournamentApi instance.
  try {
    const token = localStorage.getItem('portal_token') || localStorage.getItem('token')
    const res = await fetch(`/api/play-stay/bookings/${bookingId}/pdf`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!res.ok) {
      let msg = 'Could not download PDF'
      try { msg = (await res.json())?.message || msg } catch {}
      alert(msg)
      return
    }
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `stayplay-itinerary-${bookingId}.pdf`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  } catch (err) {
    alert('PDF download failed: ' + (err.message || err))
  }
}

function exportCsv(rows) {
  if (!rows || rows.length === 0) return
  const headers = [
    'Booking #', 'Status', 'Created',
    'Golfer', 'Email', 'Phone', '# Golfers',
    'Package', 'Play Date', 'Tee Time',
    'Hotel', 'Hotel Check-in', 'Hotel Check-out',
    'Total', 'Payment Status', 'Payment Provider',
    'Affiliate', 'UTM Source', 'UTM Medium', 'UTM Campaign',
  ]
  const lines = [headers.map(csvCell).join(',')]
  for (const b of rows) {
    lines.push([
      b.ID_NO, b.Status, b.DOE,
      b.GolferName, b.GolferEmail, b.GolferPhone, b.NumGolfers,
      b.PackageName, b.PlayDate ? String(b.PlayDate).slice(0, 10) : '',
      b.TeeTime ? String(b.TeeTime).substring(0, 5) : '',
      b.HotelName,
      b.HotelCheckin ? String(b.HotelCheckin).slice(0, 10) : '',
      b.HotelCheckout ? String(b.HotelCheckout).slice(0, 10) : '',
      Number(b.TotalPrice || 0).toFixed(2),
      b.PaymentStatus, b.PaymentProvider,
      b.AffiliateCode, b.UtmSource, b.UtmMedium, b.UtmCampaign,
    ].map(csvCell).join(','))
  }
  // BOM so Excel opens UTF-8 cleanly
  const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  const stamp = new Date().toISOString().slice(0, 10)
  a.href = url
  a.download = `stay-play-bookings-${stamp}.csv`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export default function Bookings() {
  const { t } = useLanguage()
  const [bookings, setBookings] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [statusFilter, setStatusFilter] = useState('all')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [expanded, setExpanded] = useState(null)
  const [search, setSearch] = useState('')

  function load() {
    setLoading(true)
    setError(null)
    const params = new URLSearchParams()
    if (statusFilter !== 'all') params.set('status', statusFilter)
    if (from) params.set('from', from)
    if (to) params.set('to', to)
    const qs = params.toString()
    tournamentApi.get('/play-stay/bookings' + (qs ? '?' + qs : ''))
      .then(({ data }) => setBookings(Array.isArray(data) ? data : []))
      .catch(err => setError(err.response?.data?.message || err.message))
      .finally(() => setLoading(false))
  }

  useEffect(load, [statusFilter, from, to])

  const filtered = useMemo(() => {
    if (!search) return bookings
    const q = search.toLowerCase()
    return bookings.filter(b =>
      (b.GolferName || '').toLowerCase().includes(q) ||
      (b.GolferEmail || '').toLowerCase().includes(q) ||
      (b.PackageName || '').toLowerCase().includes(q) ||
      String(b.ID_NO).includes(q)
    )
  }, [bookings, search])

  // KPIs across the currently-loaded set (already course-scoped server-side)
  const kpis = useMemo(() => {
    const total = bookings.length
    const revenue = bookings.reduce((sum, b) => sum + Number(b.TotalPrice || 0), 0)
    const upcoming = bookings.filter(b => b.PlayDate && b.Status !== 'cancelled' && new Date(b.PlayDate) >= new Date()).length
    const needsAttention = bookings.filter(b => b.Status === 'needs_attention').length
    return { total, revenue, upcoming, needsAttention }
  }, [bookings])

  return (
    <PortalShell>
      <div style={{ maxWidth: 1200, margin: '40px auto', padding: '0 20px' }}>
        <div style={{ marginBottom: 8 }}>
          <a href="/portal" style={{ fontSize: 13, color: 'var(--brand-600)', textDecoration: 'none' }}>← {t('dashboard', 'Dashboard')}</a>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 24, gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, color: 'var(--brand-600)', textTransform: 'uppercase' }}>{t('sp_stay_and_play_caps', 'Stay & Play')}</div>
            <h1 style={{ fontSize: 28, color: 'var(--secondary-950)', margin: '4px 0 0' }}>{t('sp_nav_bookings', 'Bookings')}</h1>
            <p style={{ color: 'var(--text-muted)', margin: '4px 0 0', fontSize: 14 }}>
              {t('sp_bookings_subtitle', 'All reservations for your course. Click a row for the full itinerary.')}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => exportCsv(filtered)} className="btn btn-o" style={{ padding: '8px 16px' }} disabled={filtered.length === 0}>
              {t('sp_export_csv', 'Export CSV')}
            </button>
            <button onClick={load} className="btn" style={{ padding: '8px 16px' }}>{t('sp_refresh', 'Refresh')}</button>
          </div>
        </div>

        {/* KPI strip — Needs-attention tile is clickable to filter the list */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
          <Tile label={t('total', 'Total')} value={kpis.total} onClick={statusFilter !== 'all' ? () => setStatusFilter('all') : undefined} />
          <Tile label={t('sp_kpi_upcoming', 'Upcoming')} value={kpis.upcoming} color="#16a34a" />
          <Tile
            label={t('sp_kpi_needs_attention', 'Needs attention')}
            value={kpis.needsAttention}
            color={kpis.needsAttention > 0 ? '#dc2626' : undefined}
            onClick={kpis.needsAttention > 0 ? () => setStatusFilter('needs_attention') : undefined}
          />
          <Tile label={t('sp_kpi_revenue_loaded', 'Revenue (loaded)')} value={fmtMoney(kpis.revenue)} />
        </div>

        {/* Filters */}
        <div style={filterBarStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={filterLabelStyle}>{t('status', 'Status')}</label>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={selectStyle}>
              <option value="all">{t('all', 'All')}</option>
              <option value="pending">{t('sp_status_pending', 'Pending')}</option>
              <option value="confirmed">{t('sp_status_confirmed', 'Confirmed')}</option>
              <option value="needs_attention">{t('sp_kpi_needs_attention', 'Needs attention')}</option>
              <option value="cancelled">{t('sp_status_cancelled', 'Cancelled')}</option>
              <option value="completed">{t('sp_status_completed', 'Completed')}</option>
              <option value="no_show">{t('sp_status_no_show', 'No-Show')}</option>
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={filterLabelStyle}>{t('sp_filter_play_date', 'Play date')}</label>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={selectStyle} placeholder={t('sp_filter_from', 'From')} />
            <span style={{ color: 'var(--text-muted)' }}>→</span>
            <input type="date" value={to} onChange={e => setTo(e.target.value)} style={selectStyle} placeholder={t('sp_filter_to', 'To')} />
            {(from || to) && (
              <button onClick={() => { setFrom(''); setTo('') }} className="btn btn-o" style={{ padding: '4px 10px', fontSize: 12 }}>{t('sp_clear', 'Clear')}</button>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 200 }}>
            <label style={filterLabelStyle}>{t('sp_search', 'Search')}</label>
            <input
              type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder={t('sp_bookings_search_placeholder', 'Name, email, package, or booking #')}
              style={{ ...selectStyle, flex: 1 }}
            />
          </div>
        </div>

        {error && (
          <div style={{ padding: 16, borderRadius: 8, background: '#fef2f2', color: '#991b1b', marginBottom: 16, border: '1px solid #fecaca' }}>
            {error}
          </div>
        )}

        {loading && (
          <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>{t('sp_loading_bookings', 'Loading bookings…')}</div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="card" style={{ padding: 60, textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }} aria-hidden="true">📋</div>
            <h3 style={{ margin: '0 0 8px', color: 'var(--secondary-950)' }}>{t('sp_no_bookings_yet', 'No bookings yet')}</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>
              {bookings.length === 0
                ? t('sp_no_bookings_empty', 'When customers book your Stay & Play packages, they\'ll show up here.')
                : t('sp_no_bookings_filtered', 'No bookings match the current filters.')}
            </p>
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: '#64748b' }}>
                  <th style={thStyle}>#</th>
                  <th style={thStyle}>{t('sp_th_golfer', 'Golfer')}</th>
                  <th style={thStyle}>{t('sp_th_package', 'Package')}</th>
                  <th style={thStyle}>{t('sp_filter_play_date', 'Play date')}</th>
                  <th style={thStyle}>{t('sp_th_hotel', 'Hotel')}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t('total', 'Total')}</th>
                  <th style={thStyle}>{t('status', 'Status')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(b => (
                  <BookingRow
                    key={b.ID_NO}
                    booking={b}
                    expanded={expanded === b.ID_NO}
                    onToggle={() => setExpanded(expanded === b.ID_NO ? null : b.ID_NO)}
                    onChanged={load}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </PortalShell>
  )
}

function BookingRow({ booking: b, expanded, onToggle, onChanged }) {
  const c = STATUS_COLORS[b.Status] || { bg: '#f3f4f6', fg: '#374151' }
  return (
    <>
      <tr
        onClick={onToggle}
        style={{ cursor: 'pointer', borderTop: '1px solid #f1f5f9', transition: 'background 0.15s' }}
        onMouseEnter={e => e.currentTarget.style.background = '#fafbfc'}
        onMouseLeave={e => e.currentTarget.style.background = expanded ? '#f8fafc' : 'transparent'}
      >
        <td style={tdStyle}>
          <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>#{b.ID_NO}</span>
        </td>
        <td style={tdStyle}>
          <div style={{ fontWeight: 600, color: 'var(--secondary-950)' }}>{b.GolferName || '—'}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{b.GolferEmail || ''}</div>
        </td>
        <td style={tdStyle}>{b.PackageName || '—'}</td>
        <td style={tdStyle}>
          <div>{fmtLocalDate(b.PlayDate)}</div>
          {b.TeeTime && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{String(b.TeeTime).substring(0, 5)}</div>}
        </td>
        <td style={tdStyle}>
          <div style={{ fontSize: 13 }}>{b.HotelName || '—'}</div>
          {b.HotelCheckin && b.HotelCheckout && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {fmtLocalDate(b.HotelCheckin, { month: 'short', day: 'numeric' })} → {fmtLocalDate(b.HotelCheckout, { month: 'short', day: 'numeric' })}
            </div>
          )}
        </td>
        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{fmtMoney(b.TotalPrice)}</td>
        <td style={tdStyle}>
          <span style={{
            display: 'inline-block', padding: '3px 10px', borderRadius: 999,
            background: c.bg, color: c.fg, fontSize: 11, fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: 0.4,
          }}>
            {STATUS_LABELS[b.Status] || b.Status}
          </span>
        </td>
      </tr>
      {expanded && <BookingDetails booking={b} onChanged={onChanged} />}
    </>
  )
}

function BookingDetails({ booking: b, onChanged }) {
  const [busy, setBusy] = useState(null) // 'cancel' | 'resend' | null
  const [actionMsg, setActionMsg] = useState(null)
  const [actionErr, setActionErr] = useState(null)

  const cancellable = !['cancelled', 'cancel_pending', 'cancel_failed', 'refund_pending', 'refund_ready', 'refunded', 'no_show'].includes(b.Status)
  // No-show button: only show for past-trip bookings not already in a
  // terminal/cancel status. Comparison uses local-date string to avoid
  // the same TZ pitfall we hit on the email template.
  const lastPlayDate = b.PlayDate ? String(b.PlayDate).slice(0, 10) : null
  const todayLocal = new Date(); todayLocal.setHours(0,0,0,0)
  const todayStr = todayLocal.toISOString().slice(0, 10)
  const tripPast = lastPlayDate && lastPlayDate < todayStr
  const noShowable = tripPast && ['confirmed', 'pending', 'completed', 'needs_attention'].includes(b.Status)

  async function doResend() {
    setActionMsg(null); setActionErr(null); setBusy('resend')
    try {
      const { data } = await tournamentApi.post(`/play-stay/bookings/${b.ID_NO}/resend-confirmation`)
      setActionMsg(`Confirmation re-sent to ${data.sentTo || b.GolferEmail}`)
    } catch (err) {
      setActionErr(err.response?.data?.message || err.message || 'Resend failed')
    } finally {
      setBusy(null)
    }
  }

  async function doNoShow() {
    if (!confirm(`Mark booking #${b.ID_NO} as a no-show?\n\nUse this when the golfer didn't arrive for their tee time. This is for record-keeping only — no refund is issued and the tee sheet is not changed.\n\nContinue?`)) return
    setActionMsg(null); setActionErr(null); setBusy('noshow')
    try {
      await tournamentApi.post(`/play-stay/bookings/${b.ID_NO}/mark-no-show`)
      setActionMsg('Marked as no-show.')
      onChanged?.()
    } catch (err) {
      setActionErr(err.response?.data?.message || err.message || 'Mark as no-show failed')
    } finally {
      setBusy(null)
    }
  }

  async function doCancel() {
    if (!confirm(`Cancel booking #${b.ID_NO}?\n\nThis will:\n  • Cancel the tee time(s) on your sheet\n  • Cancel the hotel reservation (if applicable)\n  • Issue a refund per the cancellation policy\n\nContinue?`)) return
    const reason = prompt('Optional reason (for your records):', '') || ''
    setActionMsg(null); setActionErr(null); setBusy('cancel')
    try {
      await tournamentApi.post(`/play-stay/bookings/${b.ID_NO}/cancel`, { reason })
      setActionMsg('Booking cancelled. Refresh the list to see updated status.')
      // Refresh the parent list so status chip updates
      onChanged?.()
    } catch (err) {
      setActionErr(err.response?.data?.message || err.message || 'Cancel failed')
    } finally {
      setBusy(null)
    }
  }
  return (
    <tr>
      <td colSpan={7} style={{ padding: 0, background: '#f8fafc' }}>
        <div style={{ padding: '20px 24px', borderTop: '1px solid #e5e7eb' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 24 }}>
            <DetailBlock label="Booking">
              <div>#{b.ID_NO}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Created {fmtLocalDate(b.DOE)}</div>
            </DetailBlock>
            <DetailBlock label="Primary golfer">
              <div>{b.GolferName || '—'}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{b.GolferEmail || ''}</div>
              {b.GolferPhone && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{b.GolferPhone}</div>}
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{b.NumGolfers} golfer{b.NumGolfers !== 1 ? 's' : ''} total</div>
            </DetailBlock>
            <DetailBlock label="Golf">
              <div>{b.PackageName || '—'}</div>
              <div style={{ fontSize: 13 }}>{fmtLocalDate(b.PlayDate)}</div>
              {b.TeeTime && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Tee time {String(b.TeeTime).substring(0, 5)}</div>}
              {b.Provider && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>Provider: {b.Provider}</div>}
              {b.BookingRef && <div style={{ fontSize: 11, color: 'var(--text-muted)', wordBreak: 'break-all' }}>Ref: {b.BookingRef}</div>}
            </DetailBlock>
            <DetailBlock label="Hotel">
              <div>{b.HotelName || '—'}</div>
              {b.HotelCheckin && (
                <div style={{ fontSize: 13 }}>
                  {fmtLocalDate(b.HotelCheckin, { weekday: 'short', month: 'short', day: 'numeric' })}
                  {' → '}
                  {fmtLocalDate(b.HotelCheckout, { weekday: 'short', month: 'short', day: 'numeric' })}
                </div>
              )}
              {b.HotelProvider && (
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                  {b.HotelProvider} · {b.HotelConfirmed ? '✓ Confirmed' : 'Pending confirmation'}
                </div>
              )}
              {b.NeedsAttentionReason && (
                <div style={{ fontSize: 11, color: '#991b1b', marginTop: 4 }}>{b.NeedsAttentionReason}</div>
              )}
            </DetailBlock>
            <DetailBlock label="Payment">
              <div style={{ fontWeight: 600 }}>{fmtMoney(b.TotalPrice)}</div>
              {b.PaymentStatus && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Status: {b.PaymentStatus}</div>}
              {b.PaymentProvider && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Via {b.PaymentProvider}</div>}
              {b.PaymentChargedAt && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Charged {fmtLocalDate(b.PaymentChargedAt)}</div>}
              {Number(b.LoyaltyDiscountAppliedCents) > 0 && (
                <div style={{ fontSize: 12, color: '#16a34a', marginTop: 4 }}>Loyalty: −{fmtMoney(b.LoyaltyDiscountAppliedCents / 100)}</div>
              )}
            </DetailBlock>
            {(b.AffiliateCode || b.UtmSource) && (
              <DetailBlock label="Attribution">
                {b.AffiliateCode && <div style={{ fontSize: 12 }}>Affiliate: <code>{b.AffiliateCode}</code></div>}
                {b.UtmSource && <div style={{ fontSize: 12 }}>Source: {b.UtmSource}</div>}
                {b.UtmMedium && <div style={{ fontSize: 12 }}>Medium: {b.UtmMedium}</div>}
                {b.UtmCampaign && <div style={{ fontSize: 12 }}>Campaign: {b.UtmCampaign}</div>}
              </DetailBlock>
            )}
          </div>

          {/* Row actions */}
          <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #e5e7eb', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <button
              onClick={() => downloadBookingPdf(b.ID_NO)}
              disabled={busy !== null}
              className="btn btn-o"
              style={{ padding: '6px 14px', fontSize: 13, cursor: 'pointer' }}
            >
              📄 Download PDF
            </button>
            <button
              onClick={doResend}
              disabled={busy !== null}
              className="btn btn-o"
              style={{ padding: '6px 14px', fontSize: 13, cursor: busy === 'resend' ? 'wait' : 'pointer' }}
            >
              {busy === 'resend' ? 'Sending…' : '✉ Resend confirmation'}
            </button>
            {noShowable && (
              <button
                onClick={doNoShow}
                disabled={busy !== null}
                style={{
                  padding: '6px 14px', fontSize: 13, fontWeight: 600,
                  background: '#fff', color: '#854d0e',
                  border: '1px solid #fde68a', borderRadius: 6,
                  cursor: busy === 'noshow' ? 'wait' : 'pointer',
                }}
              >
                {busy === 'noshow' ? 'Marking…' : '⚠ Mark as no-show'}
              </button>
            )}
            {cancellable && (
              <button
                onClick={doCancel}
                disabled={busy !== null}
                style={{
                  padding: '6px 14px', fontSize: 13, fontWeight: 600,
                  background: '#fff', color: '#dc2626',
                  border: '1px solid #fecaca', borderRadius: 6,
                  cursor: busy === 'cancel' ? 'wait' : 'pointer',
                }}
              >
                {busy === 'cancel' ? 'Cancelling…' : '✕ Cancel booking'}
              </button>
            )}
            {actionMsg && (
              <span style={{ fontSize: 12, color: '#166534', background: '#f0fdf4', padding: '4px 10px', borderRadius: 6, border: '1px solid #bbf7d0' }}>
                ✓ {actionMsg}
              </span>
            )}
            {actionErr && (
              <span style={{ fontSize: 12, color: '#991b1b', background: '#fef2f2', padding: '4px 10px', borderRadius: 6, border: '1px solid #fecaca' }}>
                ✕ {actionErr}
              </span>
            )}
          </div>
        </div>
      </td>
    </tr>
  )
}

function DetailBlock({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: 13, color: 'var(--secondary-950)', lineHeight: 1.5 }}>{children}</div>
    </div>
  )
}

function Tile({ label, value, color, onClick }) {
  const interactive = typeof onClick === 'function'
  return (
    <div
      className="card"
      onClick={onClick}
      style={{
        padding: 14,
        cursor: interactive ? 'pointer' : 'default',
        transition: interactive ? 'box-shadow 0.15s, transform 0.15s' : 'none',
      }}
      onMouseEnter={interactive ? (e => { e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.08)' }) : undefined}
      onMouseLeave={interactive ? (e => { e.currentTarget.style.boxShadow = '' }) : undefined}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={interactive ? (e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick() } }) : undefined}
    >
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: color || 'var(--secondary-950)', marginTop: 4 }}>{value}</div>
      {interactive && (
        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>Click to filter →</div>
      )}
    </div>
  )
}

const filterBarStyle = {
  display: 'flex', flexWrap: 'wrap', gap: 16,
  padding: 14, marginBottom: 16,
  background: '#fff', border: '1px solid var(--card-border)', borderRadius: 8,
}
const filterLabelStyle = { fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }
const selectStyle = {
  padding: '6px 10px', border: '1px solid var(--card-border)', borderRadius: 6,
  fontSize: 13, background: '#fff', color: 'var(--secondary-950)',
}
const thStyle = { padding: '12px 16px', textAlign: 'left', fontWeight: 700 }
const tdStyle = { padding: '14px 16px', fontSize: 14, color: 'var(--secondary-950)', verticalAlign: 'top' }
